import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useBranding } from "../../context/BrandingContext";
import ImageUploader from "../../components/common/ImageUploader";
import { Eye, Loader2, X } from "lucide-react";

const DEFAULTS_PREVIEW = {
  eyebrow: "Mega Sale · Up to 70% off · Limited Time",
  title_line_1: "Everything you need.",
  title_line_2: "Delivered fast.",
  subtitle:
    "Your one-stop marketplace for electronics, home, beauty and more — from verified sellers across India.",
  cta_label: "Shop now",
  cta_link: "/shop",
  secondary_cta_label: "Sell with us",
  secondary_cta_link: "/seller/onboard",
};

/** Predefined destinations admins can pick for the hero CTAs without
 * having to remember the storefront URL structure. The label is shown
 * in the dropdown; the value is the path that gets persisted. */
const CTA_DESTINATIONS = [
  { label: "Shop all products",        value: "/shop" },
  { label: "Shop — Electronics",       value: "/shop/electronics" },
  { label: "Shop — Mobiles",           value: "/shop/mobiles" },
  { label: "Shop — Home & Kitchen",    value: "/shop/home-kitchen" },
  { label: "Shop — Beauty",            value: "/shop/beauty" },
  { label: "Sell with us (seller onboarding)", value: "/seller/onboard" },
  { label: "Start your own store (SaaS signup)", value: "/start-store" },
  { label: "Reels feed",               value: "/reels" },
  { label: "Wishlist",                 value: "/wishlist" },
  { label: "Cart",                     value: "/cart" },
  { label: "My Account",               value: "/account" },
  { label: "Custom URL…",              value: "__custom__" },
];

const ALIGN_H = [
  { label: "Left",   value: "left" },
  { label: "Center", value: "center" },
  { label: "Right",  value: "right" },
];
const ALIGN_V = [
  { label: "Top",    value: "top" },
  { label: "Center", value: "center" },
  { label: "Bottom", value: "bottom" },
];

const empty = {
  image_url: "",
  eyebrow: "",
  title_line_1: "",
  title_line_2: "",
  subtitle: "",
  cta_label: "",
  cta_link: "",
  secondary_cta_label: "",
  secondary_cta_link: "",
  text_align: "left",
  vertical_align: "center",
};

/** Tiny "× reset" button — clicking it stores `null` so the storefront
 * falls back to the preset default for that field. */
function ResetBtn({ onReset, testid }) {
  return (
    <button
      type="button"
      onClick={onReset}
      className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-text))] inline-flex items-center gap-1 ml-2"
      data-testid={testid}
      title="Reset to default"
    >
      <X size={10} /> Reset
    </button>
  );
}

/** Destination picker — admins select from common storefront paths
 * instead of typing them. Falls back to a free-form URL input when
 * the admin picks "Custom URL…". */
function CtaPathPicker({ value, onChange, placeholder, testid }) {
  // The picker is in "preset" mode when the current value matches one of
  // the predefined destinations; otherwise it's in "custom" mode and the
  // raw text input is shown.
  const matched = CTA_DESTINATIONS.find((d) => d.value === value && d.value !== "__custom__");
  const [mode, setMode] = useState(matched ? "preset" : "custom");

  return (
    <div className="space-y-2">
      <select
        className="kb-input"
        value={mode === "preset" ? value : "__custom__"}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "__custom__") { setMode("custom"); onChange(""); }
          else { setMode("preset"); onChange(v); }
        }}
        data-testid={`${testid}-picker`}
      >
        {CTA_DESTINATIONS.map((d) => (
          <option key={d.value} value={d.value}>{d.label}</option>
        ))}
      </select>
      {mode === "custom" && (
        <input
          className="kb-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={200}
          data-testid={testid}
        />
      )}
    </div>
  );
}

export default function AdminHero() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { refresh: refreshBranding } = useBranding();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/branding");
      const hero = data.hero || {};
      setForm({
        // ?? preserves empty string ("") so admin sees their explicit
        // blank — `||` would replace empty with our placeholder default.
        image_url: hero.image_url ?? "",
        eyebrow: hero.eyebrow ?? "",
        title_line_1: hero.title_line_1 ?? "",
        title_line_2: hero.title_line_2 ?? "",
        subtitle: hero.subtitle ?? "",
        cta_label: hero.cta_label ?? "",
        cta_link: hero.cta_link ?? "",
        secondary_cta_label: hero.secondary_cta_label ?? "",
        secondary_cta_link: hero.secondary_cta_link ?? "",
        text_align: hero.text_align || "left",
        vertical_align: hero.vertical_align || "center",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load hero settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/branding/hero", form);
      toast.success("Hero saved — visible on your storefront homepage");
      await refreshBranding?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(
        Array.isArray(d)
          ? d.map((x) => x.msg || JSON.stringify(x)).join("; ")
          : d || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  /** Clear a single field back to the preset default — sends `null` to
   * the server, which removes the stored override. */
  const reset = async (k) => {
    setForm((f) => ({ ...f, [k]: "" }));
    try {
      await api.put("/admin/branding/hero", { [k]: null });
      toast.success("Field reset to default");
      await refreshBranding?.();
      // Re-pull the now-defaulted value so the input placeholder shows
      // the fallback again.
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[hsl(var(--kb-muted))]" data-testid="hero-loading">
        <Loader2 size={14} className="animate-spin" /> Loading hero settings…
      </div>
    );
  }

  return (
    <div className="max-w-5xl" data-testid="admin-hero-page">
      <div className="overline mb-2">Storefront</div>
      <div className="flex items-center justify-between gap-4 mb-8">
        <h1 className="font-display text-4xl">Homepage Hero</h1>
        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          className="kb-btn-outline !py-2 !px-4 inline-flex items-center gap-2 text-sm"
          data-testid="hero-preview-link"
        >
          <Eye size={14} /> View storefront
        </a>
      </div>

      <p className="text-sm text-[hsl(var(--kb-muted))] mb-6 max-w-2xl">
        Customize the large banner that loads on your storefront homepage. Leave any
        field blank to fall back to the platform default for your theme preset.
      </p>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left: form */}
        <div className="bg-white border border-[hsl(var(--kb-border))] p-6 space-y-5">
          <div>
            <label className="overline block mb-1">Hero background image</label>
            <ImageUploader
              value={form.image_url ? [form.image_url] : []}
              onChange={(arr) => set("image_url", arr[0] || "")}
              max={1}
              aspect="aspect-[16/9]"
              label="Upload hero image"
              testid="hero-image"
            />
            <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-1">
              Recommended: 1920×1080 or larger, JPG/PNG/WebP. A dark overlay is
              automatically added so headline text stays readable.
            </p>
          </div>

          <div>
            <label className="overline block mb-1">Eyebrow (small text above headline)</label>
            <input
              className="kb-input"
              value={form.eyebrow}
              onChange={(e) => set("eyebrow", e.target.value)}
              placeholder={DEFAULTS_PREVIEW.eyebrow}
              data-testid="hero-eyebrow"
              maxLength={120}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="overline block mb-1">Headline — line 1</label>
              <input
                className="kb-input"
                value={form.title_line_1}
                onChange={(e) => set("title_line_1", e.target.value)}
                placeholder={DEFAULTS_PREVIEW.title_line_1}
                data-testid="hero-title-1"
                maxLength={120}
              />
            </div>
            <div>
              <label className="overline block mb-1">Headline — line 2 <span className="text-[hsl(var(--kb-muted))] text-[10px]">(italic accent)</span></label>
              <input
                className="kb-input"
                value={form.title_line_2}
                onChange={(e) => set("title_line_2", e.target.value)}
                placeholder={DEFAULTS_PREVIEW.title_line_2}
                data-testid="hero-title-2"
                maxLength={120}
              />
            </div>
          </div>

          <div>
            <label className="overline block mb-1">
              Subtitle
              {form.subtitle !== "" && <ResetBtn onReset={() => reset("subtitle")} testid="hero-subtitle-reset" />}
            </label>
            <textarea
              rows={2}
              className="kb-input"
              value={form.subtitle}
              onChange={(e) => set("subtitle", e.target.value)}
              placeholder={DEFAULTS_PREVIEW.subtitle}
              data-testid="hero-subtitle"
              maxLength={240}
            />
            <p className="text-[10px] text-[hsl(var(--kb-muted))] mt-1">
              Leave the field empty and click <b>Save</b> to hide this line on the storefront.
            </p>
          </div>

          {/* Text positioning ----------------------------------------- */}
          <div className="border-t border-[hsl(var(--kb-border))] pt-4">
            <div className="overline mb-3">Hero text position</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs flex flex-col">
                <span className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">Horizontal</span>
                <select
                  className="kb-input"
                  value={form.text_align}
                  onChange={(e) => set("text_align", e.target.value)}
                  data-testid="hero-text-align"
                >
                  {ALIGN_H.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </label>
              <label className="text-xs flex flex-col">
                <span className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">Vertical</span>
                <select
                  className="kb-input"
                  value={form.vertical_align}
                  onChange={(e) => set("vertical_align", e.target.value)}
                  data-testid="hero-vertical-align"
                >
                  {ALIGN_V.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </label>
            </div>
          </div>

          <div className="border-t border-[hsl(var(--kb-border))] pt-4">
            <div className="overline mb-3">Primary call-to-action</div>
            <div className="grid md:grid-cols-2 gap-3">
              <input
                className="kb-input"
                value={form.cta_label}
                onChange={(e) => set("cta_label", e.target.value)}
                placeholder={DEFAULTS_PREVIEW.cta_label}
                data-testid="hero-cta-label"
                maxLength={40}
              />
              <CtaPathPicker
                value={form.cta_link}
                onChange={(v) => set("cta_link", v)}
                placeholder={DEFAULTS_PREVIEW.cta_link}
                testid="hero-cta-link"
              />
            </div>
          </div>

          <div className="border-t border-[hsl(var(--kb-border))] pt-4">
            <div className="overline mb-3">Secondary call-to-action <span className="text-[hsl(var(--kb-muted))] text-[10px] normal-case">(optional)</span></div>
            <div className="grid md:grid-cols-2 gap-3">
              <input
                className="kb-input"
                value={form.secondary_cta_label}
                onChange={(e) => set("secondary_cta_label", e.target.value)}
                placeholder={DEFAULTS_PREVIEW.secondary_cta_label}
                data-testid="hero-cta2-label"
                maxLength={40}
              />
              <CtaPathPicker
                value={form.secondary_cta_link}
                onChange={(v) => set("secondary_cta_link", v)}
                placeholder={DEFAULTS_PREVIEW.secondary_cta_link}
                testid="hero-cta2-link"
              />
            </div>
            <p className="text-[10px] text-[hsl(var(--kb-muted))] mt-2">
              Tip: leave the secondary label empty to hide this button.
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <button
              className="kb-btn-primary"
              onClick={save}
              disabled={saving}
              data-testid="hero-save-btn"
            >
              {saving ? "Saving…" : "Save hero"}
            </button>
          </div>
        </div>

        {/* Right: preview */}
        <div>
          <div className="overline mb-2">Live preview</div>
          <div className="relative aspect-[16/10] border border-[hsl(var(--kb-border))] overflow-hidden" data-testid="hero-preview">
            {form.image_url ? (
              <img src={form.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--kb-primary))] to-[hsl(var(--kb-accent))]" />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/60" />
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-6">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/90 mb-3 max-w-md truncate">
                {form.eyebrow || DEFAULTS_PREVIEW.eyebrow}
              </div>
              <h2 className="font-display text-2xl sm:text-3xl leading-tight font-light tracking-tight">
                {form.title_line_1 || DEFAULTS_PREVIEW.title_line_1}
                <br />
                <em className="italic text-[hsl(var(--kb-accent))]">
                  {form.title_line_2 || DEFAULTS_PREVIEW.title_line_2}
                </em>
              </h2>
              <p className="mt-3 text-white/80 text-xs leading-relaxed max-w-xs line-clamp-3">
                {form.subtitle || DEFAULTS_PREVIEW.subtitle}
              </p>
              <div className="flex gap-2 mt-4">
                <span className="kb-btn-primary !py-1.5 !px-3 text-xs">
                  {form.cta_label || DEFAULTS_PREVIEW.cta_label}
                </span>
                {(form.secondary_cta_label || DEFAULTS_PREVIEW.secondary_cta_label) && (
                  <span className="kb-btn-outline border-white text-white text-xs !py-1.5 !px-3">
                    {form.secondary_cta_label || DEFAULTS_PREVIEW.secondary_cta_label}
                  </span>
                )}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-2">
            Preview shrinks the live hero to fit — actual storefront uses full
            viewport height with bigger typography.
          </p>
        </div>
      </div>
    </div>
  );
}
